import { useClientPortalAPI } from "@certn/api";
import {
  CountrySelectFormikField,
  Grid,
  SelectFormikField,
  SelectItem,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { LocationCountryWhatsThis } from "../../../../components/LocationCountryWhatsThis";
import {
  RequestorFields,
  additionalNotificationEmailsField,
  primaryReviewerField,
  useRequestorRoleMeta,
} from "../requestor";
import { useCreateUserContext } from "./CreateUserContext";
import { CreateUserPageLayout } from "./CreateUserPageLayout";
import { RoleLearnMore } from "./RoleLearnMore";

export function CreateUserDetailsStep() {
  const { user, setUser, goToNextStep } = useCreateUserContext();
  const { t } = useTranslation();
  const { clientPortalEnableRequestorRole } = useFlags();
  const roles = useClientPortalAPI("/api/internal/client-portal/roles/");

  const { requestorRoleId, visibleRoles } = useRequestorRoleMeta({
    roles: roles?.data,
    enableRequestorRole: clientPortalEnableRequestorRole,
  });

  const schema = useMemo(
    () =>
      object({
        full_name: string().required().label(t("Full name")),
        email: string().email().required().label(t("Email address")),
        role: string().required().label(t("Role")),
        location_country: string().nullable().label(t("Location Country")),
        primary_reviewer: primaryReviewerField(requestorRoleId, t),
        additional_notification_emails: additionalNotificationEmailsField(t),
      }),
    [requestorRoleId, t]
  );

  return (
    <Formik
      initialValues={user}
      validationSchema={schema}
      onSubmit={(values) => {
        setUser(values);
        goToNextStep();
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        const isRequestorRole =
          Boolean(requestorRoleId) && values.role === requestorRoleId;
        return (
          <CreateUserPageLayout
            onComplete={handleSubmit}
            submitting={isSubmitting}
          >
            <Grid form>
              <TextInputFormikField
                testID="full-name-input"
                name="full_name"
                label={t("Full name")}
                isRequired
              />
              <TextInputFormikField
                testID="email-input"
                name="email"
                label={t("Email")}
                isRequired
              />
              <SelectFormikField
                name="role"
                label={t("Role")}
                testID="role-select-input"
                isRequired
                info={<RoleLearnMore />}
              >
                {visibleRoles?.map((role) => (
                  <SelectItem key={role.id} id={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectFormikField>
              <CountrySelectFormikField
                name="location_country"
                label={t("Location Country")}
                testID="location-country-field"
                info={<LocationCountryWhatsThis />}
              />
              {isRequestorRole ? <RequestorFields /> : null}
            </Grid>
          </CreateUserPageLayout>
        );
      }}
    </Formik>
  );
}
